import { useMemo, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import { NON_RESTRICTED_ATTRIBUTE_PROPERTIES } from "@constants/non-restricted-attribute-properties";
import { AttributeLabel, AttributeType, RpServiceAttributeProperty } from "@idw-types/data/reference-data/generic";
import { FormattedRpServiceConfigurationAttribute, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { getFilteredServiceAttributes, groupServiceAttributeProperties } from "@utils/gateway-attribute-util";
import { SortableAttributeContainer } from "../sortable-attribute-container/sortable-attribute-container";
import styles from "./sortable-attributes-container.less";
import { DEFAULT_DND_ITEM_STYLES, ITEM_BORDER_STYLE, PLACEHOLDER_BORDER_STYLE } from "@constants/dnd-constants";
import { SortableDndItem } from "@containers/common/sortable-dnd-container/sortable-dnd-item/sortable-dnd-item";
import { SortableDndContainer } from "@containers/common/sortable-dnd-container/sortable-dnd-container";
import { DragEndEvent } from "@dnd-kit/core";
import { sortNumbers } from "@utils/number-util";

type SortableAttributesContainerProps = {
    isDisabled: boolean;
    localizeStrings: boolean;
    mergeThemingAndLocalizedFile: boolean;
    selectedLanguage: LocaleTypes;
    axnuiContent: AXNUIContent;
    attributeLabels: Array<AttributeLabel>;
    canModifyBranding: boolean;
    canModifyAttributeProperties: boolean;
    canViewAttributeProperties: boolean;
    canModifyNonRestrictedAttributeProperties: boolean;
    canViewNonRestrictedAttributeProperties: boolean;
    serviceAttributeProperties: Array<RpServiceAttributeProperty>;
    attributes: Array<FormattedRpServiceConfigurationAttribute>;
    loginHintSupported: boolean;
    setAttributes: (attributes: Array<FormattedRpServiceConfigurationAttribute>, updatedRpServiceConfigurationProperties: Array<FormattedRpServiceConfigurationProperty>) => void;
    isUsingThemedUI: boolean;
    getUpdatedPropertiesForAttributeLabelUpdate: (
        attributeLabel: AttributeLabel,
        attributeType: AttributeType
    ) => Array<FormattedRpServiceConfigurationProperty>;
    removeAttribute: (attributeToRemove: FormattedRpServiceConfigurationAttribute) => void;
};

export const SortableAttributesContainer = (props: SortableAttributesContainerProps) => {
    const {
        isDisabled,
        selectedLanguage,
        localizeStrings,
        mergeThemingAndLocalizedFile,
        attributeLabels,
        axnuiContent,
        canModifyBranding,
        canModifyAttributeProperties,
        canViewAttributeProperties,
        canModifyNonRestrictedAttributeProperties,
        canViewNonRestrictedAttributeProperties,
        attributes,
        loginHintSupported,
        isUsingThemedUI,
        getUpdatedPropertiesForAttributeLabelUpdate,
        serviceAttributeProperties,
        setAttributes,
        removeAttribute
    } = props;
    const [dndActiveId, setDndActiveId] = useState(null);


    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = active.data.current.sortable.index;
            const newIndex = over.data.current.sortable.index;

            const currentAttributes = cloneDeep(attributes);
            const attributeAtNewIndex = cloneDeep(currentAttributes[newIndex]);
            const [movedAttribute] = currentAttributes.splice(oldIndex, 1);
            const updatedAttributes: Array<FormattedRpServiceConfigurationAttribute> = [];

            currentAttributes.forEach(so => {
                if (so.order > movedAttribute.order && so.order <= attributeAtNewIndex.order) {
                    updatedAttributes.push({ ...so, order: so.order - 1 });
                } else if (so.order < movedAttribute.order && so.order >= attributeAtNewIndex.order) {
                    updatedAttributes.push({ ...so, order: so.order + 1 });
                }
            });

            movedAttribute.order = attributeAtNewIndex.order;
            updatedAttributes.push(movedAttribute);
            setAttributes(updatedAttributes, null);
        }
    };

    const updateAttribute = (attribute: FormattedRpServiceConfigurationAttribute, updatedRpServiceConfigurationProperties: Array<FormattedRpServiceConfigurationProperty>) => {
        const newAttributes = attributes.map(attr => attr.attributeType?.id === attribute.attributeType?.id ? attribute : attr);
        setAttributes(newAttributes, updatedRpServiceConfigurationProperties);
    };

    const sortedAttributes = useMemo(() => {
        return attributes.sort((a, b) => sortNumbers(a.order, b.order));
    }, [attributes]);

    const attributePropertyRows = useMemo(() => {
        let allowedAttributeProperties: Array<RpServiceAttributeProperty> = [];
        if (canViewAttributeProperties) {
            allowedAttributeProperties = serviceAttributeProperties;
        } else if (canViewNonRestrictedAttributeProperties) {
            allowedAttributeProperties = serviceAttributeProperties.filter(attrProp => NON_RESTRICTED_ATTRIBUTE_PROPERTIES.includes(attrProp.name));
        } else {
            return [];
        }

        const filteredServiceAttributes = getFilteredServiceAttributes(allowedAttributeProperties, loginHintSupported);
        const result = groupServiceAttributeProperties(filteredServiceAttributes);
        return result;
    }, [
        serviceAttributeProperties,
        loginHintSupported,
        canViewAttributeProperties,
        canViewNonRestrictedAttributeProperties
    ]);


    const attributeValues = useMemo(() => {
        return sortedAttributes.map(attribute => attribute.attributeType?.id);
    }, [sortedAttributes]);

    const attributeElements: Array<(shouldApplyPlaceholder: boolean, activeId?: string) => React.ReactNode> = useMemo(() => {
        return sortedAttributes.map((attribute, index) => (
            (shouldApplyPlaceholder: boolean, activeId: string) => (
                <SortableDndItem
                    key={`attribute-${attribute.attributeType?.id}`}
                    showDragAndDropIcon={false}
                    isActiveId={shouldApplyPlaceholder && activeId === attribute.attributeType?.id}
                    id={attribute.attributeType?.id}
                    additionalStyle={{ ...DEFAULT_DND_ITEM_STYLES, ...ITEM_BORDER_STYLE }}
                    activeIdStyle={{ ...DEFAULT_DND_ITEM_STYLES, ...PLACEHOLDER_BORDER_STYLE }}
                >
                    <SortableAttributeContainer
                        index={index}
                        isDisabled={isDisabled}
                        selectedLanguage={selectedLanguage}
                        localizeStrings={localizeStrings}
                        mergeThemingAndLocalizedFile={mergeThemingAndLocalizedFile}
                        attributeLabels={attributeLabels}
                        attributePropertyRows={attributePropertyRows}
                        canModifyBranding={canModifyBranding}
                        canModifyNonRestrictedAttributeProperties={canModifyNonRestrictedAttributeProperties}
                        canViewNonRestrictedAttributeProperties={canViewNonRestrictedAttributeProperties}
                        canModifyAttributeProperties={canModifyAttributeProperties}
                        canViewAttributeProperties={canViewAttributeProperties}
                        attribute={attribute}
                        axnuiContent={axnuiContent}
                        updateAttribute={updateAttribute}
                        removeAttribute={removeAttribute}
                        isUsingThemedUI={isUsingThemedUI}
                        getUpdatedPropertiesForAttributeLabelUpdate={getUpdatedPropertiesForAttributeLabelUpdate}
                    />
                </SortableDndItem>)));
    }, [
        sortedAttributes, isDisabled, selectedLanguage,
        localizeStrings, mergeThemingAndLocalizedFile, attributeLabels,
        attributePropertyRows, canModifyBranding,
        canModifyNonRestrictedAttributeProperties, canViewNonRestrictedAttributeProperties,
        canModifyAttributeProperties, canViewAttributeProperties,
        axnuiContent, isUsingThemedUI, dndActiveId
    ]);

    return (
        <div className={styles.sortableAttributesContainer}>
            <SortableDndContainer
                isDisabled={isDisabled}
                useDragOverlay={true}
                internalDragAndDropElements={attributeElements}
                internalDragAndDropElementsValues={attributeValues}
                handleDragEnd={handleDragEnd}
                activeId={dndActiveId}
                setActiveId={setDndActiveId}
            />
        </div>
    );
};